// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

import type { ExtractedDoc } from './extract.ts';

export interface Chunk {
  docIndex: number;
  /** Display trail, e.g. "Guide › Installing". */
  heading: string;
  anchor: string;
  /** The chunk body — this is what gets shown as a snippet. */
  text: string;
  /** The exact string that is embedded (heading trail + body). */
  embedInput: string;
}

export interface ChunkOptions {
  targetChars: number;
  overlapChars: number;
  minChars: number;
}

export const DEFAULT_CHUNK_OPTIONS: ChunkOptions = {
  targetChars: 700,
  overlapChars: 120,
  minChars: 60,
};

/**
 * Split on sentence ends, keeping the terminator. Falls back to hard-slicing a
 * run of text that contains no sentence boundary at all (minified prose, a long
 * table row) so one pathological line cannot produce a single enormous chunk.
 */
export function splitSentences(text: string, maxRun: number): string[] {
  const rough = text.match(/[^.!?]+(?:[.!?]+["')\]]*|$)/g) ?? [text];
  const out: string[] = [];
  for (const piece of rough) {
    const s = piece.trim();
    if (!s) continue;
    if (s.length <= maxRun) {
      out.push(s);
      continue;
    }
    for (let i = 0; i < s.length; i += maxRun) out.push(s.slice(i, i + maxRun));
  }
  return out;
}

/** Greedy sentence packing to `targetChars` with a trailing-character overlap. */
export function windowText(text: string, options: ChunkOptions): string[] {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  if (clean.length <= options.targetChars) return [clean];

  const sentences = splitSentences(clean, options.targetChars);
  const chunks: string[] = [];
  let cur = '';

  for (const sentence of sentences) {
    if (cur && cur.length + sentence.length + 1 > options.targetChars) {
      chunks.push(cur);
      const tail = options.overlapChars > 0 ? cur.slice(-options.overlapChars) : '';
      const cut = tail.indexOf(' ');
      cur = cut >= 0 ? tail.slice(cut + 1) : '';
    }
    cur = cur ? `${cur} ${sentence}` : sentence;
  }
  if (cur) chunks.push(cur);

  // A final fragment shorter than minChars is folded back rather than shipped as
  // a near-empty result row.
  if (chunks.length > 1) {
    const last = chunks[chunks.length - 1];
    if (last.length < options.minChars) {
      chunks.pop();
      chunks[chunks.length - 1] = `${chunks[chunks.length - 1]} ${last}`;
    }
  }
  return chunks;
}

/**
 * Prepending the heading trail to the embedded text is the single cheapest
 * retrieval win available: it gives an orphaned paragraph the context ("Billing ›
 * Refunds") that the paragraph itself assumes the reader already has.
 */
export function buildEmbedInput(title: string, heading: string, body: string): string {
  const context = heading ? `${title} › ${heading}` : title;
  return context ? `${context}. ${body}` : body;
}

export function chunkDoc(doc: ExtractedDoc, docIndex: number, options: ChunkOptions): Chunk[] {
  const chunks: Chunk[] = [];
  for (const section of doc.sections) {
    const heading = section.heading.filter(Boolean).join(' › ');
    for (const body of windowText(section.text, options)) {
      if (body.length < options.minChars && doc.sections.length > 1) continue;
      chunks.push({
        docIndex,
        heading,
        anchor: section.anchor,
        text: body,
        embedInput: buildEmbedInput(doc.title, heading, body),
      });
    }
  }
  return chunks;
}

export type UrlStyle = 'keep' | 'html' | 'clean' | 'directory';

export interface UrlOptions {
  /** Site-root prefix, e.g. '/docs/'. */
  base: string;
  /** Leading path segment to drop, e.g. 'docs/' or 'src/content/'. */
  stripPrefix: string;
  style: UrlStyle;
}

export const DEFAULT_URL_OPTIONS: UrlOptions = { base: '/', stripPrefix: '', style: 'clean' };

/** Map a source file path to the URL the built site will serve it at. */
export function docUrl(path: string, options: UrlOptions): string {
  let p = path.replace(/\\/g, '/').replace(/^\.?\//, '');
  const strip = options.stripPrefix.replace(/^\.?\//, '');
  if (strip && p.startsWith(strip)) p = p.slice(strip.length);
  p = p.replace(/^\/+/, '');

  const dot = p.lastIndexOf('.');
  const stem = dot > p.lastIndexOf('/') ? p.slice(0, dot) : p;

  let tail: string;
  switch (options.style) {
    case 'keep':
      tail = p;
      break;
    case 'html':
      tail = `${stem}.html`;
      break;
    case 'directory':
      tail = stem.replace(/(^|\/)index$/, '') || '';
      tail = tail ? `${tail}/` : '';
      break;
    case 'clean':
    default:
      tail = stem.replace(/(^|\/)index$/, '$1');
      break;
  }

  const base = options.base.endsWith('/') ? options.base : `${options.base}/`;
  return `${base}${tail}`.replace(/([^:])\/{2,}/g, '$1/');
}
